// src/data/complianceDeadlines.js
//
// Compliance deadlines shown anywhere in the app (dashboard Timeline menu,
// etc.) AND on the marketing site (index.html / pricing.html / eu-ai-act.html
// — a separate repo/host). The REAL single source of truth is now the
// deadlines.json file served by the API at GET /api/deadlines (see
// server-deadlines-route-snippet.js) — both this app and the marketing
// website fetch that at runtime via fetchDeadlines() below.
//
// The DEADLINES array here is kept ONLY as an offline fallback for when the
// API is unreachable (network hiccup, API deploy in progress, etc.) — it is
// NOT where you add new regulations anymore. To add/change a deadline, edit
// deadlines.json on the server; both sites pick it up on next page load,
// no code change or redeploy needed here. Keep this array roughly in sync
// as a backup, but it's secondary now.
//
// Status ("Upcoming" vs "In Force") is computed from today's date at render
// time — never hardcode ⏳/✅ or the word "Upcoming"/"In Force" anywhere else.

const DEADLINES_API_URL =
  (process.env.REACT_APP_API_BASE || 'https://api.quantumaishield.com') + '/api/deadlines';

export const DEADLINES = [
  {
    id: 'eu-ai-act-transparency',
    law: 'EU AI Act',
    title: 'Transparency Obligations (Articles 50–52)',
    description: 'AI-generated content labeling, deepfake disclosure, chatbot disclosure rules.',
    date: '2026-08-02',
    penalty: {
      amount: '€15,000,000',
      percent: '3%',
      basis: 'global annual turnover',
      rule: 'whichever is higher (large companies) / whichever is lower (SMEs)',
      display: 'Up to €15M or 3% of global annual turnover',
      articleRef: 'Article 99(4)',
    },
  },
  {
    id: 'eu-ai-act-watermark-grace',
    law: 'EU AI Act',
    title: 'AI Content Labeling Deadline',
    description: 'Grace period ends for machine-readable watermarking of AI-generated content already on the market. New "nudifier" app ban also takes effect.',
    date: '2026-12-02',
    penalty: {
      amount: '€15,000,000',
      percent: '3%',
      basis: 'global annual turnover',
      rule: 'whichever is higher (large companies) / whichever is lower (SMEs)',
      display: 'Up to €15M or 3% of global annual turnover',
      articleRef: 'Article 99(4) — same tier as Article 50',
    },
  },
  {
    id: 'au-privacy-adm',
    law: 'Australia Privacy Act',
    title: 'Automated Decision-Making (ADM) Disclosure',
    description: 'Privacy policies must disclose when AI/automated tools make or substantially support decisions significantly affecting individuals.',
    date: '2026-12-10',
    penalty: {
      amount: 'AUD $50,000,000',
      percent: null,
      basis: 'or 30% of adjusted turnover / 3x benefit obtained, whichever greater, for serious/repeated breaches',
      rule: 'serious or repeated interferences with privacy',
      display: 'Up to AUD $50M for serious breaches',
      articleRef: 'Privacy Act civil penalty provisions',
    },
  },
  {
    id: 'kr-ai-basic-act-fines',
    law: 'South Korea AI Basic Act',
    title: 'High-Impact AI — Fine Enforcement Begins',
    description: 'Grace period ends; fines apply for high-impact AI violations such as failing to disclose AI use.',
    date: '2027-01-22',
    penalty: {
      amount: '~30,000,000 KRW (~$21,000 USD)',
      percent: null,
      basis: 'per violation',
      rule: 'one-year grace period through Jan 2027; MSIT generally defers except serious harm cases',
      display: 'Up to ~$21,000 USD per violation',
      articleRef: 'AI Basic Act enforcement decree',
    },
  },
  {
    id: 'eu-ai-act-annex3',
    law: 'EU AI Act',
    title: 'High-Risk AI Systems (Standalone)',
    description: 'Annex III high-risk systems (hiring, credit scoring, biometrics, education, immigration, law enforcement) must meet full compliance requirements.',
    date: '2027-12-02',
    penalty: {
      amount: '€15,000,000',
      percent: '3%',
      basis: 'global annual turnover',
      rule: 'whichever is higher (large companies) / whichever is lower (SMEs)',
      display: 'Up to €15M or 3% of global annual turnover',
      articleRef: 'Article 99(4)',
    },
  },
  {
    id: 'eu-ai-act-annex1',
    law: 'EU AI Act',
    title: 'High-Risk AI (Embedded in Regulated Products)',
    description: 'AI embedded in already-regulated products (medical devices, machinery, etc.) must meet full compliance requirements.',
    date: '2028-08-02',
    penalty: {
      amount: '€15,000,000',
      percent: '3%',
      basis: 'global annual turnover',
      rule: 'whichever is higher (large companies) / whichever is lower (SMEs)',
      display: 'Up to €15M or 3% of global annual turnover',
      articleRef: 'Article 99(4)',
    },
  },
];

/**
 * Compute display status for a single deadline, relative to "now".
 *
 * 3-tier urgency escalation (compliance deadlines need lead time to act on,
 * unlike a personal trial countdown, so thresholds are wider):
 *   > 90 days out   → upcoming    (⏳ neutral/informational)
 *   31-90 days out  → approaching (⚠️ amber, start planning)
 *   <= 30 days out  → urgent      (🚨 red, act now)
 *   <= 0 days out   → in-force    (✅ already active/enforced)
 *
 * @param {string} dateStr - ISO date, e.g. '2026-08-02'
 * @returns {{status: 'in-force'|'urgent'|'approaching'|'upcoming', icon: string, label: string, daysUntil: number}}
 */
export function getDeadlineStatus(dateStr, now = new Date()) {
  const deadline = new Date(`${dateStr}T00:00:00Z`);
  const daysUntil = Math.ceil((deadline.getTime() - now.getTime()) / 86_400_000);

  if (daysUntil <= 0) {
    return { status: 'in-force', icon: '✅', label: 'In Force', daysUntil: 0 };
  }
  if (daysUntil <= 30) {
    return { status: 'urgent', icon: '🚨', label: 'Urgent', daysUntil };
  }
  if (daysUntil <= 90) {
    return { status: 'approaching', icon: '⚠️', label: 'Approaching', daysUntil };
  }
  return { status: 'upcoming', icon: '⏳', label: 'Upcoming', daysUntil };
}

/**
 * Full deadline list with computed status attached — what components should
 * render. Pass in the (possibly API-fetched) deadlines array; defaults to
 * the local fallback if omitted, e.g. getDeadlinesWithStatus() with no args.
 */
export function getDeadlinesWithStatus(deadlines = DEADLINES, now = new Date()) {
  return deadlines
    .map((d) => ({ ...d, ...getDeadlineStatus(d.date, now) }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

/**
 * The single deadline a banner/spotlight slot should feature RIGHT NOW.
 * Auto-advances the moment a deadline passes — no manual "switch to next
 * deadline" edit ever needed. Picks the soonest one that isn't already
 * in-force; if every deadline has passed, falls back to showing the most
 * recently enforced one (better than showing nothing).
 */
export function getFeaturedDeadline(deadlines = DEADLINES, now = new Date()) {
  const withStatus = getDeadlinesWithStatus(deadlines, now);
  return withStatus.find((d) => d.status !== 'in-force')
    ?? withStatus[withStatus.length - 1]
    ?? null;
}

/**
 * Fetch the canonical deadline list from the API. Falls back to the local
 * DEADLINES array (above) if the request fails, so the UI never breaks —
 * it just temporarily shows slightly-stale data until the API is reachable.
 *
 * Usage in a component:
 *   const [deadlines, setDeadlines] = useState(DEADLINES); // fallback while loading
 *   useEffect(() => { fetchDeadlines().then(setDeadlines); }, []);
 *   const withStatus = getDeadlinesWithStatus(deadlines);
 */
export async function fetchDeadlines() {
  try {
    const res = await fetch(DEADLINES_API_URL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) throw new Error('empty/invalid response');
    return data;
  } catch (err) {
    console.warn('[complianceDeadlines] fetch failed, using local fallback:', err.message);
    return DEADLINES;
  }
}

// CommonJS fallback (in case a non-ESM script needs this)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { DEADLINES, getDeadlineStatus, getDeadlinesWithStatus, getFeaturedDeadline, fetchDeadlines };
}